import { useState, useEffect, useCallback } from "react";
import { useDomainContext } from "~/api/domainContext.hooks";
import { useWebsiteChatState } from "~/hooks/website/useWebsiteChat";
import { buildPlatformDomain } from "~/lib/domain";
import type { Website } from "@shared";
import type { DomainAssignment } from "./DomainPicker";

// ============================================================================
// Mock recommendations for dev/test (no AI server needed)
// ============================================================================

const MOCK_RECOMMENDATIONS: Website.DomainRecommendations.DomainRecommendations = {
  state: "new_recommended",
  recommendations: [
    {
      domain: buildPlatformDomain("coolstartup"),
      subdomain: "coolstartup",
      fullUrl: buildPlatformDomain("coolstartup"),
      score: 95,
      reasoning: "Clean, memorable name that matches your brand",
      source: "suggestion",
      availability: "available",
    },
    {
      domain: buildPlatformDomain("mybusiness"),
      subdomain: "mybusiness",
      fullUrl: buildPlatformDomain("mybusiness"),
      score: 88,
      reasoning: "Professional and straightforward",
      source: "suggestion",
      availability: "available",
    },
    {
      domain: buildPlatformDomain("testproject"),
      subdomain: "testproject",
      fullUrl: buildPlatformDomain("testproject"),
      score: 80,
      reasoning: "Simple and descriptive",
      source: "suggestion",
      availability: "available",
    },
  ],
  topRecommendation: {
    domain: buildPlatformDomain("coolstartup"),
    subdomain: "coolstartup",
    fullUrl: buildPlatformDomain("coolstartup"),
    score: 95,
    reasoning: "Clean, memorable name that matches your brand",
    source: "suggestion",
    availability: "available",
  },
};

const isDev = import.meta.env.DEV;

// ============================================================================
// useDomainPickerData - All data fetching & derived values
// ============================================================================

export function useDomainPickerData(websiteId: number | undefined) {
  const [useMockData, setUseMockData] = useState(false);

  // Fetch domain context from Rails (existing domains, credits, assigned URL)
  const { data: context, isLoading, error } = useDomainContext(websiteId);

  // Get AI recommendations from website graph state
  const liveRecommendations = useWebsiteChatState("domainRecommendations") as
    | Website.DomainRecommendations.DomainRecommendations
    | undefined;

  const recommendations = useMockData ? MOCK_RECOMMENDATIONS : liveRecommendations;

  // Derived values
  const assignedUrl = context?.assigned_url ?? null;
  const credits = context?.platform_subdomain_credits;
  const isUnlimited = credits?.unlimited ?? false;
  const isOutOfCredits = !isUnlimited && credits?.remaining === 0;
  const creditsRemaining = isUnlimited
    ? Infinity
    : (credits?.remaining ?? 0);

  const toggleMockData = useCallback(() => setUseMockData((v) => !v), []);

  return {
    context,
    recommendations,
    isLoading,
    error,
    assignedUrl,
    isOutOfCredits,
    creditsRemaining,
    isUnlimited,
    // Dev-only mock toggle
    isDev,
    useMockData,
    toggleMockData,
  };
}

// ============================================================================
// useSelectionInit - Initialize selection from assigned URL or recommendations
// ============================================================================

interface UseSelectionInitOptions {
  isLoading: boolean;
  assignedUrl: { domain: string; path?: string | null; domain_id: number } | null;
  topRecommendation: Website.DomainRecommendations.DomainRecommendation | null | undefined;
  onSelectionChange: (selection: DomainAssignment | null) => void;
  markPersisted: (selection: DomainAssignment) => void;
}

export function useSelectionInit({
  isLoading,
  assignedUrl,
  topRecommendation,
  onSelectionChange,
  markPersisted,
}: UseSelectionInitOptions) {
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (hasInitialized || isLoading) return;

    // Priority 1: Use assigned URL from website
    if (assignedUrl) {
      const initial: DomainAssignment = {
        domain: assignedUrl.domain,
        origin: "existing",
        existingDomainId: assignedUrl.domain_id,
      };
      onSelectionChange(initial);
      markPersisted(initial);
      setHasInitialized(true);
      return;
    }

    // Priority 2: Use AI top recommendation. A recommendation is only ever a
    // brand-new name to claim — it never carries an existingDomainId, so the
    // selection stays "unpersisted" and deploy remains gated until the user
    // actually picks/claims it.
    if (topRecommendation) {
      onSelectionChange({
        domain: topRecommendation.domain,
        origin: topRecommendation.source,
      });
      setHasInitialized(true);
    }
  }, [hasInitialized, isLoading, assignedUrl, topRecommendation, onSelectionChange, markPersisted]);

  return { hasInitialized };
}
